import * as Prismic from '@prismicio/client';

import { Lazy, pipe } from 'fp-ts/function';
import * as TE from 'fp-ts/TaskEither';
import * as O from 'fp-ts/Option';
import { HttpError, HttpTask } from '@core/http';
import { PrismicContent, PrismicDocument, PrismicPageType } from '@core/prismic/model';
import { getConfigValue } from '@shared/utils/config';
import { getAccountTypeCookie } from '@shared/utils/cookies';

import * as Sentry from '@sentry/react';
import { Language } from '@shared/modules/translation';

const getRepositoryName = (): string => {
  return pipe(
    getConfigValue('VITE_PRISMIC_REPOSITORY'),
    O.getOrElse(() => 'vegetag'),
  );
};

const client = Prismic.createClient(Prismic.getRepositoryEndpoint(getRepositoryName()));

function transformPrismicRequest<T>(request: Lazy<Promise<T>>): HttpTask<T> {
  return TE.tryCatch(request, err => {
    console.error(err);

    if (err instanceof Prismic.PrismicError && !err.response) {
      return HttpError.notFound;
    }

    Sentry.captureException(err);

    return HttpError.default;
  });
}

function getPrismicLocale(locale?: string): string {
  return locale === 'en' ? 'en-gb' : 'fr-fr';
}

export function getPrismicDocumentByUID<R extends PrismicContent>(
  type: PrismicPageType,
  uid: string,
  locale: Language,
): HttpTask<PrismicDocument<R>> {
  const getByLang = (lang: string, targetUid: string) =>
    transformPrismicRequest(() => client.getByUID<PrismicDocument<R>>(type, targetUid, { lang }));

  const accountType = getAccountTypeCookie();

  const tryGetDocument = (targetUid: string) =>
    pipe(
      getByLang(getPrismicLocale(locale), targetUid),
      TE.orElse(() => getByLang('*', targetUid)),
    );
  if (accountType) {
    const prefixedUid = `${accountType}-${uid}`;

    return pipe(
      tryGetDocument(prefixedUid),
      TE.orElse(() => tryGetDocument(uid)),
    );
  }

  return tryGetDocument(uid);
}

export function getAll<R extends PrismicContent>(
  locale: string,
  params?: Parameters<Prismic.Client['dangerouslyGetAll']>[0],
): HttpTask<Array<PrismicDocument<R>>> {
  return transformPrismicRequest(() =>
    client.dangerouslyGetAll<PrismicDocument<R>>({ ...params, lang: getPrismicLocale(locale) }),
  );
}
